"use strict";
/**
 * @file intl.ts
 *
 * Framework-agnostic ICU MessageFormat runtime for the server side (e.g.
 * rendering transactional emails in the recipient's locale). Wraps
 * `@formatjs/intl` so the same catalog format and message API used by
 * `react-intl` on the client also work in Node.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createI18n = createI18n;
const intl_1 = require("@formatjs/intl");
const locales_1 = require("./locales");
/**
 * Builds a locale-resolving `getIntl` accessor over a set of message catalogs.
 * Each app calls this once with its own compiled catalogs; the returned
 * function memoizes one {@link IntlShape} per resolved locale. Unknown locales
 * resolve to the closest supported one (see {@link resolveLocale}) and fall
 * back per key to the default catalog, so a partially translated catalog
 * (e.g. a fresh Phrase pull) serves the default-locale text for its gaps
 * instead of raw message ids.
 *
 * @param catalogs - Per-locale message catalogs keyed by BCP-47 tag.
 *
 * @returns A `getIntl(locale)` accessor returning the memoized {@link IntlShape}
 *   for the closest supported locale.
 */
function createI18n(catalogs) {
    const cache = (0, intl_1.createIntlCache)();
    const instances = new Map();
    return function getIntl(locale) {
        const resolved = (0, locales_1.resolveLocale)(locale);
        const cached = instances.get(resolved);
        if (cached)
            return cached;
        const messages = {
            ...catalogs[locales_1.DEFAULT_LOCALE],
        };
        for (const [id, message] of Object.entries(catalogs[resolved] ?? {})) {
            // An empty string means "not translated yet"; keep the default text.
            if (message === '')
                continue;
            messages[id] = message;
        }
        const intl = (0, intl_1.createIntl)({ locale: resolved, defaultLocale: locales_1.DEFAULT_LOCALE, messages }, cache);
        instances.set(resolved, intl);
        return intl;
    };
}
